import { Play, Pause, RotateCcw, RotateCw, Download } from 'lucide-react'
import { useState, useRef, useEffect } from 'react'
import { Button } from '#app/components/ui/button.tsx'
import { Progress } from '#app/components/ui/progress.tsx'

interface Props {
	audioUrl?: string
	duration: number
	onTimeUpdate?: (time: number) => void
	onPlayStateChange?: (isPlaying: boolean) => void
	className?: string
	showSkipButtons?: boolean
	skipAmount?: number
	showSpeedControl?: boolean
	showDownload?: boolean
}

export function AudioPlayer({
	audioUrl,
	duration,
	onTimeUpdate,
	onPlayStateChange,
	className = '',
	showSkipButtons = true,
	skipAmount = 5,
	showSpeedControl = true,
	showDownload = true,
}: Props) {
	const [isPlaying, setIsPlaying] = useState(false)
	const [currentTime, setCurrentTime] = useState(0)
	const [isDragging, setIsDragging] = useState(false)
	const [playbackRate, setPlaybackRate] = useState(1)
	const audioRef = useRef<HTMLAudioElement>(null)
	const animationFrameRef = useRef<number>(0)
	const simulationStartTimeRef = useRef<number>(0)

	// Format time in mm:ss
	const formatTime = (seconds: number) => {
		const mins = Math.floor(seconds / 60)
		const secs = Math.floor(seconds % 60)
		return `${mins}:${secs.toString().padStart(2, '0')}`
	}

	// Format milliseconds to seconds for display
	const formatMilliseconds = (ms: number) => {
		return formatTime(ms / 1000)
	}

	// Handle audio element events
	useEffect(() => {
		const audio = audioRef.current
		if (!audio) return

		const handleTimeUpdate = () => {
			if (!isDragging) {
				const time = audio.currentTime
				setCurrentTime(time * 1000) // Convert to milliseconds
				onTimeUpdate?.(time * 1000)
			}
		}

		const handleEnded = () => {
			setIsPlaying(false)
			onPlayStateChange?.(false)
		}

		const handlePlay = () => {
			setIsPlaying(true)
			onPlayStateChange?.(true)
		}

		const handlePause = () => {
			setIsPlaying(false)
			onPlayStateChange?.(false)
		}

		audio.addEventListener('timeupdate', handleTimeUpdate)
		audio.addEventListener('ended', handleEnded)
		audio.addEventListener('play', handlePlay)
		audio.addEventListener('pause', handlePause)

		return () => {
			audio.removeEventListener('timeupdate', handleTimeUpdate)
			audio.removeEventListener('ended', handleEnded)
			audio.removeEventListener('play', handlePlay)
			audio.removeEventListener('pause', handlePause)
		}
	}, [isDragging, onTimeUpdate, onPlayStateChange])

	// Simulation for demo mode when no audio URL
	useEffect(() => {
		if (!audioUrl && isPlaying) {
			simulationStartTimeRef.current = Date.now() - currentTime

			const updateSimulation = () => {
				const elapsed = Date.now() - simulationStartTimeRef.current
				if (elapsed >= duration) {
					// End of simulation
					setCurrentTime(duration)
					setIsPlaying(false)
					onTimeUpdate?.(duration)
					onPlayStateChange?.(false)
					return
				}

				setCurrentTime(elapsed)
				onTimeUpdate?.(elapsed)

				if (isPlaying) {
					animationFrameRef.current = requestAnimationFrame(updateSimulation)
				}
			}

			animationFrameRef.current = requestAnimationFrame(updateSimulation)

			return () => {
				if (animationFrameRef.current) {
					cancelAnimationFrame(animationFrameRef.current)
				}
			}
		}
	}, [
		audioUrl,
		isPlaying,
		currentTime,
		duration,
		onTimeUpdate,
		onPlayStateChange,
	])

	// Handle play/pause
	const handlePlayPause = () => {
		const audio = audioRef.current

		if (isPlaying) {
			// Pause
			if (audio && audioUrl) {
				audio.pause()
			}
			setIsPlaying(false)
			onPlayStateChange?.(false)
		} else {
			// Play
			if (audio && audioUrl) {
				void audio.play().catch((error) => {
					console.error('Failed to play audio:', error)
				})
			} else {
				// Demo mode - update simulation start time
				simulationStartTimeRef.current = Date.now() - currentTime
			}
			setIsPlaying(true)
			onPlayStateChange?.(true)
		}
	}

	// Skip forward/backward
	const handleSkip = (seconds: number) => {
		const audio = audioRef.current
		const skipMs = seconds * 1000
		const newTime = Math.max(0, Math.min(duration, currentTime + skipMs))

		if (audio && audioUrl) {
			audio.currentTime = newTime / 1000
		} else {
			// Demo mode
			setCurrentTime(newTime)
			simulationStartTimeRef.current = Date.now() - newTime
			onTimeUpdate?.(newTime)
		}
	}

	// Handle scrubber change
	const handleScrubberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newTime = parseFloat(e.target.value)
		setCurrentTime(newTime)

		const audio = audioRef.current
		if (audio && audioUrl) {
			audio.currentTime = newTime / 1000
		} else {
			// Demo mode
			simulationStartTimeRef.current = Date.now() - newTime
		}

		onTimeUpdate?.(newTime)
	}

	const handleScrubberMouseUp = () => {
		setIsDragging(false)
	}

	// Handle playback speed
	const handleSpeedChange = () => {
		const speeds = [0.5, 0.75, 1, 1.25, 1.5, 2]
		const currentIndex = speeds.indexOf(playbackRate)
		const nextIndex = (currentIndex + 1) % speeds.length
		const newSpeed = speeds[nextIndex]

		if (newSpeed !== undefined) {
			setPlaybackRate(newSpeed)
			const audio = audioRef.current
			if (audio) {
				audio.playbackRate = newSpeed
			}
		}
	}

	return (
		<div className={`bg-background ${className}`}>
			{/* Hidden audio element for real audio playback */}
			{audioUrl && <audio ref={audioRef} src={audioUrl} preload="metadata" />}

			<div className="flex w-full flex-col gap-2">
				{/* Top Row - Speed Control, Audio Controls, Download */}
				<div className="flex w-full items-center justify-between">
					{/* Left - Speed Control */}
					{showSpeedControl ? (
						<Button
							variant="ghost"
							size="sm"
							className="text-muted-foreground hover:text-foreground flex h-8 w-8 items-center justify-center p-0"
							onClick={handleSpeedChange}
							title={`Speed: ${playbackRate}x`}
						>
							<span className="text-xs font-medium">{playbackRate}x</span>
						</Button>
					) : (
						<div className="flex h-8 w-8 items-center justify-center" />
					)}

					{/* Center - Audio Controls */}
					<div className="flex items-center gap-2">
						{showSkipButtons && (
							<Button
								variant="ghost"
								size="sm"
								className="text-muted-foreground hover:text-foreground flex h-8 w-8 items-center justify-center p-0"
								onClick={() => handleSkip(-skipAmount)}
							>
								<RotateCcw className="h-4 w-4" />
							</Button>
						)}

						<Button
							variant="default"
							size="sm"
							onClick={handlePlayPause}
							className="flex h-8 w-8 items-center justify-center rounded-full border-0 bg-white p-0 text-black hover:bg-white/90"
						>
							{isPlaying ? (
								<Pause className="h-4 w-4" />
							) : (
								<Play className="ml-0.5 h-4 w-4" />
							)}
						</Button>

						{showSkipButtons && (
							<Button
								variant="ghost"
								size="sm"
								className="text-muted-foreground hover:text-foreground flex h-8 w-8 items-center justify-center p-0"
								onClick={() => handleSkip(skipAmount)}
							>
								<RotateCw className="h-4 w-4" />
							</Button>
						)}
					</div>

					{/* Right - Download Button */}
					{showDownload ? (
						<Button
							variant="ghost"
							size="sm"
							className="text-muted-foreground hover:text-foreground flex h-8 w-8 items-center justify-center p-0"
						>
							<Download className="h-4 w-4" />
						</Button>
					) : (
						<div className="flex h-8 w-8 items-center justify-center" />
					)}
				</div>

				{/* Progress Bar Row */}
				<div className="flex w-full items-center gap-3">
					<span className="text-muted-foreground w-8 text-right text-xs">
						{formatMilliseconds(currentTime)}
					</span>
					<div className="group relative flex-1">
						<Progress
							value={(currentTime / duration) * 100}
							className="h-1 cursor-pointer"
						/>
						<input
							type="range"
							className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
							min={0}
							max={duration}
							value={currentTime}
							onChange={handleScrubberChange}
							onMouseDown={() => setIsDragging(true)}
							onMouseUp={handleScrubberMouseUp}
						/>
					</div>
					<span className="text-muted-foreground w-8 text-xs">
						{formatMilliseconds(duration)}
					</span>
				</div>
			</div>
		</div>
	)
}
